import type { ReviewBlock, ReviewOutlineNode } from './adapter/types';
import { OutlineTree } from './OutlineTree';
import { SidebarBlockEditor } from './SidebarBlockEditor';

interface SidebarEditTabProps {
  isEditing: boolean;
  editingBlock: ReviewBlock | null;
  isSavingEdit: boolean;
  isEditingEffectiveDate: boolean;
  onSave: (newText: string) => Promise<void>;
  onCancel: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onTextChange: (text: string) => void;

  outline: ReviewOutlineNode[];
  editedBlockIds: Set<string>;
  onEditBlock: (block: ReviewBlock) => void;
  openSections: Record<string, boolean>;
  onToggleSection: (id: string, open: boolean) => void;
}

/**
 * "Edit" tab body of the review sidebar. Shows the single-block editor while a
 * block is open for editing, otherwise the document outline for picking a block
 * to edit.
 *
 * Both halves are model-agnostic — they take normalized blocks and outline
 * nodes — so this tab serves the JSON and fixed-DOCX documents alike.
 */
export function SidebarEditTab({
  isEditing,
  editingBlock,
  isSavingEdit,
  isEditingEffectiveDate,
  onSave,
  onCancel,
  onDirtyChange,
  onTextChange,
  outline,
  editedBlockIds,
  onEditBlock,
  openSections,
  onToggleSection,
}: SidebarEditTabProps) {
  if (isEditing && editingBlock) {
    return (
      <SidebarBlockEditor
        block={editingBlock}
        initialText={editingBlock.text}
        onSave={onSave}
        onCancel={onCancel}
        onDirtyChange={onDirtyChange}
        onTextChange={onTextChange}
        isSaving={isSavingEdit}
        isEffectiveDate={isEditingEffectiveDate}
      />
    );
  }

  return (
    <div className="px-3 py-4">
      <OutlineTree
        nodes={outline}
        editedBlockIds={editedBlockIds}
        activeBlockId={editingBlock?.id}
        onEditBlock={onEditBlock}
        openSections={openSections}
        onToggleSection={onToggleSection}
      />
    </div>
  );
}
